import { useState, useEffect, useCallback, type ChangeEvent } from "react";
import ShowcaseForm from "../components/showcase/ShowcaseForm";
import InstagramPostsDialog from "../components/showcase/InstagramPostsDialog";
import CommentsDialog from "../components/showcase/CommentsDialog";
import { getShowcases, deleteShowcase } from "../services/showcaseService";
import withUser from "@components/withUser";
import { getStylists, updateStylistDetails } from "@services/stylistService";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Box, Dialog, DialogTitle, DialogContent, Button, Chip, FormControlLabel, Switch, Typography, Alert } from "@mui/material";
import Grid from "@mui/material/Grid";
import NoStylist from "@components/NoStylist";
import LoadingSpinner from "@components/LoadingSpinner";
import { syncInstagramPosts, getInstagramPosts, initiateInstagramAuth, checkInstagramTokenStatus, disconnectInstagram } from "@services/instagramService";
import { isMobileDevice, openOAuthPopup, monitorPopup } from "../utils/mobileOAuthHandler";
import InstagramIcon from '@mui/icons-material/Instagram';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import DisconnectIcon from '@mui/icons-material/LinkOff';
import StylistSelector from "@components/StylistSelector";
import CommentIcon from '@mui/icons-material/Comment';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import type { Showcase } from "@app-types/models/showcase";
import type { Stylist } from "@app-types/models/stylist";
import type { InstagramPost, InstagramTokenStatus } from "@app-types/models/instagram";

const ShowcaseManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { stylistId } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [showcases, setShowcases] = useState<Showcase[]>([]);
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [selectedStylist, setSelectedStylist] = useState<string | null>(null);
  const [selectedShowcase, setSelectedShowcase] = useState<Showcase | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showcaseToDelete, setShowcaseToDelete] = useState<string | null>(null);
  const [isInstagramSyncing, setIsInstagramSyncing] = useState(false);
  const [showInstagramPosts, setShowInstagramPosts] = useState(false);
  const [instagramPosts, setInstagramPosts] = useState<InstagramPost[]>([]);
  const [selectedPosts, setSelectedPosts] = useState<string[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [isInstagramConnected, setIsInstagramConnected] = useState(false);
  const [instagramTokenStatus, setInstagramTokenStatus] = useState<InstagramTokenStatus | null>(null);
  const [isCheckingToken, setIsCheckingToken] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [isLoadingStylists, setIsLoadingStylists] = useState(false);
  const [isLoadingShowcases, setIsLoadingShowcases] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [isTogglingAutoSync, setIsTogglingAutoSync] = useState(false);

  const selectedStylistData = stylists.find(s => s._id === selectedStylist) || null;
  const autoContentSync = selectedStylistData?.autoContentSync;
  const hasInstagram = Boolean(selectedStylistData?.instagramUsername);

  const formatRelative = (date?: string) => {
    if (!date) return null;
    const d = new Date(date);
    const diffMs = d.getTime() - Date.now();
    const diffDays = Math.round(diffMs / (24 * 60 * 60 * 1000));
    if (diffDays === 0) return t('showcaseManagement.autoSyncToday', { defaultValue: 'today' });
    if (diffDays > 0) return t('showcaseManagement.autoSyncInDays', { count: diffDays, defaultValue: `in ${diffDays} days` });
    return t('showcaseManagement.autoSyncDaysAgo', { count: Math.abs(diffDays), defaultValue: `${Math.abs(diffDays)} days ago` });
  };

  const handleToggleAutoSync = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!selectedStylist) return;
    const nextEnabled = event.target.checked;
    setIsTogglingAutoSync(true);
    try {
      await updateStylistDetails({ _id: selectedStylist, autoContentSync: JSON.stringify({ enabled: nextEnabled }) });
      setStylists(prev => prev.map(s => s._id === selectedStylist
        ? { ...s, autoContentSync: { ...(s.autoContentSync || {}), enabled: nextEnabled } }
        : s));
      toast.success(t(nextEnabled ? 'showcaseManagement.autoSyncEnabled' : 'showcaseManagement.autoSyncDisabled', {
        defaultValue: nextEnabled ? 'Auto content sync enabled' : 'Auto content sync disabled'
      }));
    } catch (error) {
      console.error('Error toggling auto content sync:', error);
      toast.error(t('showcaseManagement.autoSyncError', { defaultValue: 'Failed to update setting' }));
    } finally {
      setIsTogglingAutoSync(false);
    }
  };

  const handleCaptionChange = useCallback((postId: string, newCaption: string) => {
    setInstagramPosts(prevPosts => 
      prevPosts.map(post => 
        post.id === postId 
          ? { ...post, caption: newCaption }
          : post
      )
    );
  }, []);

  const checkInstagramConnection = useCallback(async (stylistId: string) => {
    if (!stylistId) return;

    setIsCheckingToken(true);
    try {
      const tokenStatus = await checkInstagramTokenStatus(stylistId);
      setIsInstagramConnected(tokenStatus.hasToken as boolean);
      setInstagramTokenStatus(tokenStatus);

      // Show warning if expiring soon
      if (tokenStatus.isExpiringSoon && !tokenStatus.isExpired && tokenStatus.hasToken) {
        toast.warning(
          t('showcase.instagramExpiringSoon', {
            days: tokenStatus.expiresIn,
            defaultValue: `Instagram connection expires in ${tokenStatus.expiresIn} days. Please reconnect to avoid disruption.`
          })
        );
      }
    } catch (error) {
      console.error("Error checking Instagram connection:", error);
      setIsInstagramConnected(false);
      setInstagramTokenStatus(null);
    } finally {
      setIsCheckingToken(false);
    }
  }, [t]);

  useEffect(() => {
    if (selectedStylist) {
      checkInstagramConnection(selectedStylist);
    }
  }, [selectedStylist, checkInstagramConnection]);

  const fetchShowcases = useCallback(async () => {
    if (selectedStylist) {
      setIsLoadingShowcases(true);
      try {
        // Backend returns a plain Showcase[] array; the service's ShowcasesResponse
        // return type is inaccurate, so cast to preserve the existing runtime shape.
        const data = await getShowcases(selectedStylist) as unknown as Showcase[];
        setShowcases(data);
      } catch (error) {
        console.error("Error fetching showcases:", error);
      } finally {
        setIsLoadingShowcases(false);
      }
    }
  }, [selectedStylist]);

  useEffect(() => {
    fetchStylists();
  }, []);

  useEffect(() => {
    if (selectedStylist) {
      fetchShowcases();
    }
  }, [selectedStylist, fetchShowcases]);

  useEffect(() => {
    if (!showModal) {
      fetchShowcases();
    }
  }, [showModal]);

  const fetchStylists = async () => {
    setIsLoadingStylists(true);
    try {
      const data = await getStylists();
      setStylists(data);
      if (data.length > 0) {
        const stylistToSelect = stylistId || data[0]._id;
        setSelectedStylist(stylistToSelect);
      }
    } catch (error) {
      console.error("Error fetching stylists:", error);
    } finally {
      setIsLoadingStylists(false);
    }
  };

  const handleStylistSelect = (stylistId: string) => {
    setSelectedStylist(stylistId);
    navigate(`/showcase-management/${stylistId}`);
  };

  const handleInstagramAuth = async () => {
    if (!selectedStylist) return;

    setIsLoadingPosts(true);
    try {
      // For mobile popup flow, redirect to callback handler page
      const returnUrl = isMobileDevice()
        ? `${window.location.origin}/oauth-callback`
        : window.location.href;

      // Initiate Instagram auth with redirect URL
      const { authUrl } = await initiateInstagramAuth(selectedStylist, returnUrl);

      if (!authUrl) {
        throw new Error('No auth URL received from backend');
      }

      // Mobile browser: Use popup to prevent Instagram app intercept
      if (isMobileDevice()) {
        console.log('📱 Mobile browser detected - using popup approach');

        // CRITICAL: Open popup BEFORE any async operations (iOS requirement)
        const popup = openOAuthPopup(authUrl);

        // Check if popup was blocked
        if (!popup || popup.closed) {
          console.warn('⚠️ Popup was blocked by browser');
          setIsLoadingPosts(false);

          // Fallback: Navigate to intermediate page
          navigate('/oauth-redirect', {
            state: {
              authUrl: authUrl,
              returnPath: location.pathname
            }
          });
          return;
        }

        console.log('✅ OAuth popup opened successfully');

        // Monitor popup for completion
        monitorPopup(
          popup,
          (token) => {
            // Success: Token will be in URL, reload to process it
            console.log('✅ OAuth completed successfully');
            setIsLoadingPosts(false);
            // Reload to process the callback
            window.location.reload();
          },
          (error) => {
            // Error: Show message
            console.error('❌ OAuth popup error:', error);
            setIsLoadingPosts(false);
            toast.error(t('showcase.instagramAuthError', 'Failed to connect Instagram. Please try again.'));
          }
        );

      } else {
        // Desktop browser: Use traditional redirect (works perfectly)
        console.log('🖥️ Desktop browser detected - using traditional redirect');
        window.location.href = authUrl;
      }

    } catch (error) {
      console.error("Error with Instagram integration:", error);
      setIsLoadingPosts(false);
      toast.error(t('showcase.instagramAuthError', 'Failed to connect Instagram. Please try again.'));
    }
  };

  const handlePostSelection = (postId: string) => {
    setSelectedPosts(prev => {
      if (prev.includes(postId)) {
        return prev.filter(id => id !== postId);
      } else {
        return [...prev, postId];
      }
    });
  };

  const handleSyncSelectedPosts = async () => {
    if (!selectedStylist || selectedPosts.length === 0) return;
    
    setIsInstagramSyncing(true);
    try {
      // Get the selected posts with their updated captions
      const postsToSync = selectedPosts.map(postId => {
        const post = instagramPosts.find(p => p.id === postId);
        return {
          id: postId,
          caption: post!.caption || ''
        };
      });

      // syncInstagramPosts is typed as (stylistId, postIds: string[]); at runtime it
      // forwards the array of { id, caption } objects unchanged, so cast to match.
      await syncInstagramPosts(selectedStylist, postsToSync as unknown as string[]);
      toast.success(t('showcaseManagement.instagramSyncSuccess'));
      fetchShowcases();
      setShowInstagramPosts(false);
    } catch (error) {
      console.error("Error syncing selected Instagram posts:", error);

      const err = error as { response?: { status?: number; data?: { code?: string; message?: string } } };
      // Check for duplicate Instagram post error (409 status or DUPLICATE_INSTAGRAM_POST code)
      if (err.response?.status === 409 ||
          err.response?.data?.code === 'DUPLICATE_INSTAGRAM_POST' ||
          err.response?.data?.message?.includes('already been uploaded')) {
        toast.error(t('showcaseManagement.duplicateInstagramPost'));
      } else {
        toast.error(err.response?.data?.message || t('showcaseManagement.instagramSyncError'));
      }
    } finally {
      setIsInstagramSyncing(false);
    }
  };

  const handleDeleteShowcase = (id: string) => {
    setShowcaseToDelete(id);
    setConfirmDelete(true);
  };

  const confirmDeleteShowcase = async () => {
    if (showcaseToDelete) {
      try {
        await deleteShowcase(showcaseToDelete);
        fetchShowcases();
      } catch (error) {
        console.error("Error deleting showcase:", error);
      } finally {
        setConfirmDelete(false);
        setShowcaseToDelete(null);
      }
    }
  };

  const handleOpenInstagramPosts = useCallback(async () => {
    if (!selectedStylist) return;
    
    setIsLoadingPosts(true);
    try {
      // At runtime the endpoint returns { posts }, not the typed { data }; cast to match.
      const { posts } = await getInstagramPosts(selectedStylist) as unknown as { posts: InstagramPost[] };
      setInstagramPosts(posts);
      setShowInstagramPosts(true);
    } catch (error) {
      console.error("Error fetching Instagram posts:", error);
    } finally {
      setIsLoadingPosts(false);
    }
  }, [selectedStylist]);

  const handleDisconnectInstagram = async () => {
    if (!selectedStylist) return;
    
    try {
      await disconnectInstagram(selectedStylist);
      setIsInstagramConnected(false);
      setConfirmDisconnect(false);
      setShowInstagramPosts(false); // Close the Instagram posts dialog
    } catch (error) {
      console.error("Error disconnecting Instagram:", error);
    }
  };

  const handleCommentUpdate = useCallback(async () => {
    if (selectedStylist) {
      setIsLoadingShowcases(true);
      try {
        // Backend returns a plain Showcase[] array (see fetchShowcases); cast to preserve runtime shape.
        const data = await getShowcases(selectedStylist) as unknown as Showcase[];
        setShowcases(data);
        // Update the selected showcase with the latest data
        if (selectedShowcase) {
          const updatedShowcase = data.find(s => s._id === selectedShowcase._id);
          if (updatedShowcase) {
            setSelectedShowcase(updatedShowcase);
          }
        }
      } catch (error) {
        console.error("Error fetching showcases:", error);
      } finally {
        setIsLoadingShowcases(false);
      }
    }
  }, [selectedStylist, selectedShowcase]);

  // Check for Instagram authentication success in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const instagramSuccess = searchParams.get('instagram_success');
    
    if (instagramSuccess === 'true' && selectedStylist) {
      // Clear the URL parameters
      navigate(`/showcase-management/${selectedStylist}`, { replace: true });
      // Set Instagram as connected
      setIsInstagramConnected(true);
    }
  }, [location, selectedStylist, navigate]);

  if (isLoadingStylists) {
    return (
      <LoadingSpinner />
    );
  }

  if (!isLoadingStylists && stylists.length === 0) {
    return <NoStylist />;
  }

  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 },
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      boxSizing: 'border-box'
    }}>
      <StylistSelector
        stylists={stylists}
        selectedStylistId={selectedStylist as string | undefined}
        onStylistSelect={handleStylistSelect as (value: Stylist | string) => void}
        layout="wrap"
        showEmail={true}
        variant="id"
      />
      
      {selectedStylist && (
        <>
          <Box sx={{
            mb: 3,
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Button
              variant="contained"
              onClick={() => {
                setSelectedShowcase(null);
                setShowModal(true);
              }}
              sx={{
                flexShrink: 0,
                minWidth: { xs: '100%', sm: 'auto' }
              }}
            >
              {t('showcaseManagement.createShowcase')}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<InstagramIcon />}
              onClick={isInstagramConnected ? handleOpenInstagramPosts : handleInstagramAuth}
              disabled={isInstagramSyncing || isCheckingToken}
              color="primary"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 1,
                minWidth: 0,
                maxWidth: { xs: '100%', sm: 'auto' }
              }}
            >
              {isInstagramConnected
                ? t('showcaseManagement.viewInstagramPosts')
                : t('showcaseManagement.connectInstagram')}
              {isInstagramConnected && (
                <Chip
                  icon={
                    instagramTokenStatus?.isExpiringSoon ? (
                      <WarningIcon />
                    ) : (
                      <CheckCircleIcon />
                    )
                  }
                  label={
                    instagramTokenStatus?.isExpiringSoon
                      ? t('showcaseManagement.instagramExpiring', {
                          days: instagramTokenStatus.expiresIn,
                          defaultValue: `Expires in ${instagramTokenStatus.expiresIn} days`
                        })
                      : t('showcaseManagement.instagramConnected')
                  }
                  color={instagramTokenStatus?.isExpiringSoon ? "warning" : "success"}
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
              {!isInstagramConnected && instagramTokenStatus?.isExpired && (
                <Chip
                  label={t('showcaseManagement.instagramExpired', {
                    defaultValue: 'Expired (60 days)'
                  })}
                  color="error"
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </Button>
            {isInstagramConnected && (
              <Button
                variant="outlined"
                startIcon={<DisconnectIcon />}
                onClick={() => setConfirmDisconnect(true)}
                color="error"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 1,
                  minWidth: 0,
                  maxWidth: { xs: '100%', sm: 'auto' }
                }}
              >
                {t('showcaseManagement.disconnectInstagram')}
              </Button>
            )}
          </Box>

          {hasInstagram && (
            <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, maxWidth: 600 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoContentSync?.enabled !== false}
                    onChange={handleToggleAutoSync}
                    disabled={isTogglingAutoSync}
                  />
                }
                label={t('showcaseManagement.autoSyncLabel', {
                  defaultValue: 'Automatic biweekly Instagram sync'
                })}
              />
              <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {t('showcaseManagement.autoSyncDescription', {
                  defaultValue: 'Automatically pull new posts from Instagram every 2 weeks and add them as showcases.'
                })}
              </Typography>
              {(autoContentSync?.lastRunAt || (autoContentSync?.nextRunAt && autoContentSync?.enabled !== false)) && (
                <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 1 }}>
                  {autoContentSync?.lastRunAt && t('showcaseManagement.autoSyncLastRun', {
                    when: formatRelative(autoContentSync.lastRunAt),
                    defaultValue: `Last sync: ${formatRelative(autoContentSync.lastRunAt)}`
                  })}
                  {autoContentSync?.lastRunAt && autoContentSync?.nextRunAt && autoContentSync?.enabled !== false && ' • '}
                  {autoContentSync?.nextRunAt && autoContentSync?.enabled !== false && t('showcaseManagement.autoSyncNextRun', {
                    when: formatRelative(autoContentSync.nextRunAt),
                    defaultValue: `Next sync: ${formatRelative(autoContentSync.nextRunAt)}`
                  })}
                </Typography>
              )}
              {autoContentSync?.lastRunStatus === 'failed' && autoContentSync?.lastRunError && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t('showcaseManagement.autoSyncLastError', {
                    error: autoContentSync.lastRunError,
                    defaultValue: `Last sync failed: ${autoContentSync.lastRunError}`
                  })}
                </Alert>
              )}
            </Box>
          )}

          <Dialog
            open={showModal}
            onClose={() => setShowModal(false)}
          >
            <DialogTitle>
              {selectedShowcase ? t('showcaseManagement.editShowcase') : t('showcaseManagement.createShowcase')}
            </DialogTitle>
            <DialogContent>
              <ShowcaseForm
                selectedStylist={selectedStylist}
                showcase={selectedShowcase}
                closeModal={() => setShowModal(false)}
              />
            </DialogContent>
          </Dialog>

          <Dialog
            open={confirmDelete}
            onClose={() => setConfirmDelete(false)}
          >
            <DialogTitle>{t('commonConfirmationDialog.confirmDeleteTitle')}</DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                {t('commonConfirmationDialog.confirmDeleteMessage')}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={() => setConfirmDelete(false)}>
                  {t('commonConfirmationDialog.cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={confirmDeleteShowcase}
                >
                  {t('commonConfirmationDialog.confirm')}
                </Button>
              </Box>
            </DialogContent>
          </Dialog>

          <Dialog
            open={confirmDisconnect}
            onClose={() => setConfirmDisconnect(false)}
          >
            <DialogTitle>{t('showcaseManagement.confirmDisconnectTitle')}</DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                {t('showcaseManagement.confirmDisconnectMessage')}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={() => setConfirmDisconnect(false)}>
                  {t('commonConfirmationDialog.cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDisconnectInstagram}
                >
                  {t('showcaseManagement.disconnect')}
                </Button>
              </Box>
            </DialogContent>
          </Dialog>

          <InstagramPostsDialog
            open={showInstagramPosts}
            onClose={() => setShowInstagramPosts(false)}
            posts={instagramPosts}
            selectedPosts={selectedPosts}
            onPostSelection={handlePostSelection}
            onSync={handleSyncSelectedPosts}
            isLoading={isLoadingPosts}
            isSyncing={isInstagramSyncing}
            onCaptionChange={handleCaptionChange}
            onDisconnect={() => setConfirmDisconnect(true)}
          />

          <CommentsDialog
            open={showComments}
            onClose={() => {
              setShowComments(false);
              setSelectedShowcase(null);
            }}
            showcase={selectedShowcase}
            onCommentUpdate={handleCommentUpdate}
          />

          {isLoadingShowcases ? (
            <LoadingSpinner />
          ) : (
            <Grid container spacing={2} sx={{
              width: '100%',
              maxWidth: '100%'
            }}>
              {showcases.map((showcase) => (
                <Grid size={{ xs: 12, md: 6 }} key={showcase._id}>
                  <Box sx={{
                    p: 2,
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    display: 'flex',
                    gap: 2,
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box'
                  }}>
                    <Box sx={{ flex: 1, height: '100%' }}>
                      {showcase.media.length > 0 &&
                        (showcase.media[0].mediaType === "image" ? (
                          <img
                            src={`${import.meta.env.VITE_R2_ENDPOINT}/${showcase.media[0].key}`}
                            alt="Showcase Media"
                            style={{ width: "100%", height: "100%" }}
                          />
                        ) : (
                          <video
                            src={`${import.meta.env.VITE_R2_ENDPOINT}/${showcase.media[0].key}`}
                            controls
                            style={{ width: "100%", height: "100%" }}
                          />
                        ))}
                    </Box>
                    <Box sx={{ flex: 1, height: '100%' }}>
                      <Box sx={{ mb: 1 }}>
                        {new Date(showcase.createdAt as string).toLocaleDateString()}
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        {showcase.content.length > 20
                          ? `${showcase.content.substring(0, 20)}...`
                          : showcase.content}
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        {showcase.status === "pending" && (
                          <span style={{ color: "orange" }}>●</span>
                        )}
                        {showcase.status === "approved" && (
                          <span style={{ color: "green" }}>●</span>
                        )}
                        {showcase.status === "rejected" && (
                          <span style={{ color: "red" }}>●</span>
                        )}
                        {t(`showcaseManagement.${showcase.status}`)}
                        {showcase.postType === 'brochure' && (
                          <Chip
                            size="small"
                            label={t('showcaseManagement.brochure')}
                            color="warning"
                            variant="outlined"
                            sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </Box>
                      {showcase.status === "rejected" && (
                        <Box sx={{ mb: 1 }}>
                          {t('showcaseManagement.reason')}: {showcase.rejectionReason as string}
                        </Box>
                      )}
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          {t('showcaseManagement.likes')}: {(showcase.likes as unknown as unknown[]).length}
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          {t('showcaseManagement.comments')}: {showcase.comments!.length}
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          {t('showcaseManagement.shares')}: {(showcase.shares as unknown[]).length}
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          {t('showcaseManagement.clicks')}: {(showcase.clicks as unknown[]).length}
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setSelectedShowcase(showcase);
                            setShowModal(true);
                          }}
                        >
                          {t('showcaseManagement.edit')}
                        </Button>
                        <Button 
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteShowcase(showcase._id)}
                        >
                          {t('showcaseManagement.delete')}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<CommentIcon />}
                          onClick={() => {
                            setSelectedShowcase(showcase);
                            setShowComments(true);
                          }}
                        >
                          {t('showcaseManagement.manageComments')}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

const WrappedShowcaseManagementPage = withUser(ShowcaseManagementPage);
export default WrappedShowcaseManagementPage;
